import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';

/* ============================================================
   NCR — daftar dan detail.

   Riwayatnya append-only, dan halaman detail menampilkannya sebagai
   linimasa penuh alih-alih ringkasan. Yang perlu terbaca bukan
   "status sekarang" melainkan bagaimana ia sampai ke situ.
   ============================================================ */

type Ncr = {
  id: string;
  ncrNo: string;
  title: string;
  severity: string;
  state: string;
  raisedAt: string;
  dueAt: string | null;
  closedAt: string | null;
  activity: { activityCode: string } | null;
  discipline: { code: string } | null;
  contractor: { name: string } | null;
};

export const NCR_STATE: Record<string, string> = {
  OPEN: 'Terbuka',
  RESPONDED: 'Ditanggapi',
  REJECTED: 'Tanggapan ditolak',
  IN_PROGRESS: 'Perbaikan berjalan',
  READY_FOR_VERIFY: 'Menunggu verifikasi',
  CLOSED: 'Tertutup',
};

const SEV_CHIP: Record<string, string> = { MAJOR: 'crit', MINOR: 'warn', OBSERVATION: '' };

export function Ncrs({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [ncrs, setNcrs] = useState<Ncr[] | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ ncrs: Ncr[] }>(
        `/quality/ncrs?projectId=${projectId}${openOnly ? '&open=true' : ''}`,
      );
      setNcrs(r.ncrs);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load NCRs.');
    }
  }, [projectId, openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!ncrs) return <Loading rows={4} />;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Non-conformance</h1>
          <p>Diurutkan menurut keparahan lalu umur — yang paling berat dan paling lama lebih dulu.</p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            Hanya yang terbuka
          </label>
        </div>
      </div>

      {ncrs.length === 0 ? (
        <Empty
          title={openOnly ? 'Tidak ada NCR terbuka' : 'Belum ada NCR'}
          body={
            openOnly
              ? 'Semua ketidaksesuaian sudah ditutup lewat verifikasi.'
              : 'NCR diterbitkan dari inspeksi yang gagal, atau langsung dari lapangan.'
          }
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>NCR</th>
                <th>Judul</th>
                <th>Keparahan</th>
                <th>Status</th>
                <th>Aktivitas</th>
                <th>Umur</th>
                <th>Tenggat</th>
              </tr>
            </thead>
            <tbody>
              {ncrs.map((n) => {
                const age = Math.round(
                  (Date.parse(today) - Date.parse(n.raisedAt.slice(0, 10))) / 86_400_000,
                );
                const overdue = n.dueAt != null && n.dueAt.slice(0, 10) < today && !n.closedAt;
                return (
                  <tr key={n.id} onClick={() => nav(`/ncrs/${n.id}`)}>
                    <td className="num">{n.ncrNo}</td>
                    <td>{n.title}</td>
                    <td>
                      <span className={`chip ${SEV_CHIP[n.severity] ?? ''}`}>{n.severity}</span>
                    </td>
                    <td>{NCR_STATE[n.state] ?? n.state}</td>
                    <td>{n.activity?.activityCode ?? '—'}</td>
                    <td className={age > 60 && !n.closedAt ? 'neg' : ''}>{age} hari</td>
                    <td className={overdue ? 'neg' : ''}>{n.dueAt ? fmtDate(n.dueAt) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ============================================================ */

type Detail = {
  ncr: {
    id: string;
    ncrNo: string;
    title: string;
    description: string;
    severity: string;
    state: string;
    raisedAt: string;
    dueAt: string | null;
    closedAt: string | null;
    raisedBy: { name: string };
    activity: { activityCode: string; name: string } | null;
    discipline: { code: string; name: string } | null;
    contractor: { name: string } | null;
    itpItem: { seq: number; description: string; itp: { code: string; title: string } } | null;
  };
  actions: { action: string; comment: string | null; actedAt: string; actor: { name: string } }[];
};

/** Cermin tabel perpindahan di quality-core.ts. */
const ACTIONS: Record<string, { action: string; label: string; primary?: boolean }[]> = {
  OPEN: [{ action: 'RESPOND', label: 'Ajukan tanggapan', primary: true }],
  RESPONDED: [
    { action: 'APPROVE', label: 'Setujui perbaikan', primary: true },
    { action: 'REJECT', label: 'Tolak tanggapan' },
  ],
  REJECTED: [{ action: 'RESPOND', label: 'Ajukan tanggapan baru', primary: true }],
  IN_PROGRESS: [{ action: 'READY', label: 'Nyatakan selesai', primary: true }],
  READY_FOR_VERIFY: [
    { action: 'VERIFY', label: 'Verifikasi & tutup', primary: true },
    { action: 'REOPEN', label: 'Verifikasi gagal' },
  ],
  CLOSED: [],
};

const ACTION_LABEL: Record<string, string> = {
  RESPOND: 'menanggapi',
  REJECT: 'menolak tanggapan',
  APPROVE: 'menyetujui perbaikan',
  START: 'memulai perbaikan',
  READY: 'menyatakan selesai',
  VERIFY: 'memverifikasi dan menutup',
  REOPEN: 'membuka kembali',
  COMMENT: 'berkomentar',
};

export function NcrDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Detail>(`/quality/ncrs/${id}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the NCR.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: string) {
    const needs = ['RESPOND', 'REJECT', 'REOPEN'].includes(action);
    const comment = needs
      ? window.prompt('Alasannya — wajib, dan ikut tersimpan di riwayat:')
      : window.prompt('Catatan (opsional):') ?? '';
    if (needs && !comment?.trim()) return;

    setBusy(true);
    try {
      const r = await api.post<{ from: string; to: string }>(`/quality/ncrs/${id}/actions`, {
        action,
        comment,
      });
      setNotice(`${NCR_STATE[r.from] ?? r.from} → ${NCR_STATE[r.to] ?? r.to}`);
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const n = data.ncr;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{n.ncrNo}</h1>
          <p>{n.title}</p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <span className={`chip ${SEV_CHIP[n.severity] ?? ''}`}>{n.severity}</span>
          <span className={`chip ${n.state === 'CLOSED' ? 'good' : 'warn'}`}>
            {NCR_STATE[n.state] ?? n.state}
          </span>
          {(ACTIONS[n.state] ?? []).map((a) => (
            <button
              key={a.action}
              className={a.primary ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              disabled={busy}
              onClick={() => act(a.action)}
            >
              {a.label}
            </button>
          ))}
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act('COMMENT')}>
            Komentar
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/ncrs')}>
            Kembali
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {n.state !== 'CLOSED' && n.activity && n.severity === 'MAJOR' && (
        <div className="banner">
          Selama NCR ini terbuka, baris BOQ pada aktivitas {n.activity.activityCode} tidak akan
          disertifikasi — sepanjang kebijakan proyek masih menahan NCR Major.
        </div>
      )}

      <div className="grid-2">
        <Panel title="Ketidaksesuaian">
          <p style={{ whiteSpace: 'pre-wrap' }}>{n.description}</p>
          <div className="tbl-wrap" style={{ marginTop: 16 }}>
            <table className="tbl">
              <tbody>
                <tr className="no-click">
                  <td>Diterbitkan</td>
                  <td>
                    {fmtDate(n.raisedAt)} · {n.raisedBy.name}
                  </td>
                </tr>
                <tr className="no-click">
                  <td>Tenggat</td>
                  <td>{n.dueAt ? fmtDate(n.dueAt) : '—'}</td>
                </tr>
                <tr className="no-click">
                  <td>Aktivitas</td>
                  <td>{n.activity ? `${n.activity.activityCode} — ${n.activity.name}` : '—'}</td>
                </tr>
                <tr className="no-click">
                  <td>Disiplin</td>
                  <td>{n.discipline?.name ?? '—'}</td>
                </tr>
                <tr className="no-click">
                  <td>Kontraktor</td>
                  <td>{n.contractor?.name ?? '—'}</td>
                </tr>
                <tr className="no-click">
                  <td>Titik ITP</td>
                  <td>
                    {n.itpItem ? `${n.itpItem.itp.code} #${n.itpItem.seq} — ${n.itpItem.description}` : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Riwayat" note="Append-only. Tidak ada baris yang pernah ditimpa.">
          {data.actions.length === 0 ? (
            <p className="hint">Belum ada tindakan sejak diterbitkan.</p>
          ) : (
            <div className="ladder">
              {data.actions.map((a, i) => (
                <div className="lstep" key={i}>
                  <span className="ldot done">{i + 1}</span>
                  <div className="lbody">
                    <div className="lrole">
                      {a.actor.name} {ACTION_LABEL[a.action] ?? a.action}
                    </div>
                    <div className="lmeta">{fmtDate(a.actedAt)}</div>
                    {a.comment && <div className="lcomment">{a.comment}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}
